import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, X, RefreshCw, CreditCard, ArrowRight, ArrowLeft, Check, CheckCircle2, FileText, ClipboardList } from 'lucide-react';
import PaymentMethodModal from './PaymentMethodModal';
import { ConsultationFeeDisplay } from './ConsultationFeeDisplay';
import { PaymentStatus } from './PaymentStatus';
import { buildApiUrl } from '../services/apiHelpers';

/**
 * PrescriptionPaymentWorkflow - Complete payment flow modal
 * Redesigned with premium glass-morphism medical UI
 * All original functionality preserved.
 */
export function PrescriptionPaymentWorkflow({
  prescriptionId, clinicId, patientName, onClose, onPaymentComplete
}) {
  const [step, setStep] = useState('OVERVIEW');
  const [feeData, setFeeData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const [lastErrorCode, setLastErrorCode] = useState(null);
  const loadingTimeoutRef = React.useRef();

  const logWorkflowState = (action, details = {}) => {
    console.log(`%c[PaymentWorkflow] ${action}`, 'color: #2563eb; font-weight: bold; background: #e0f2fe; padding: 2px 6px; border-radius: 3px;', { prescriptionId, clinicId, patientName, step, timestamp: new Date().toLocaleTimeString(), ...details });
  };

  useEffect(() => {
    console.group('PrescriptionPaymentWorkflow Initialized');
    logWorkflowState('COMPONENT_MOUNTED');
    console.groupEnd();
    return () => console.log('PrescriptionPaymentWorkflow Unmounted');
  }, []);

  useEffect(() => {
    if (!prescriptionId || !clinicId) { setError('Missing prescription or clinic ID.'); setIsLoading(false); return; }
    let retryAttempt = 0;
    const maxRetries = 3;
    let isMounted = true;
    let fetchInProgress = false;
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      if (isMounted && isLoading) { setIsLoading(false); setError('Request timed out. Please try again.'); }
    }, 15000);

    const fetchFeeData = async () => {
      if (fetchInProgress || !isMounted) return;
      fetchInProgress = true;
      try {
        if (isMounted) { setIsLoading(true); setError(null); setRetryCount(0); }
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        const response = await axios.get(
          buildApiUrl(`prescriptions/${prescriptionId}/consultation-fee`),
          { headers: { 'X-Clinic-Id': clinicId }, signal: controller.signal, timeout: 10000 }
        );
        clearTimeout(timeoutId);
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        if (isMounted) { setFeeData(response.data); setError(null); setRetryCount(0); setLastErrorCode(null); setIsLoading(false); fetchInProgress = false; }
      } catch (err) {
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
        fetchInProgress = false;
        if (!isMounted) return;
        const status = err.response?.status;
        const errorCode = err.response?.data?.errorCode;
        const serverMessage = err.response?.data?.message;
        let userFriendlyMessage = '';
        let shouldRetry = false;
        let retryDelay = 0;
        if (status === 404 || errorCode === 'PRESCRIPTION_NOT_FOUND') {
          userFriendlyMessage = `Prescription #${prescriptionId} was not found.`; shouldRetry = false; setLastErrorCode('PRESCRIPTION_NOT_FOUND');
        } else if (status === 403 || errorCode === 'UNAUTHORIZED_ACCESS') {
          userFriendlyMessage = `Access denied: prescription belongs to a different clinic.`; shouldRetry = false; setLastErrorCode('UNAUTHORIZED_ACCESS');
        } else if (status === 400 || errorCode === 'INVALID_REQUEST') {
          userFriendlyMessage = `Invalid Request: ${serverMessage || 'Check parameters.'}`; shouldRetry = false; setLastErrorCode('INVALID_REQUEST');
        } else if (status >= 500) {
          userFriendlyMessage = `Server error encountered. Retrying…`; shouldRetry = true; retryDelay = Math.pow(2, retryAttempt + 1) * 500; setLastErrorCode('SERVER_ERROR');
        } else if (err.code === 'ECONNABORTED' || err.code === 'ECONNREFUSED') {
          userFriendlyMessage = `Connection problem. Retrying…`; shouldRetry = true; retryDelay = Math.pow(2, retryAttempt + 1) * 500; setLastErrorCode('CONNECTION_ERROR');
        } else {
          userFriendlyMessage = `${err.message || 'Something went wrong'}`; shouldRetry = retryAttempt < maxRetries && !err.response; setLastErrorCode('UNKNOWN_ERROR');
        }
        if (shouldRetry && retryAttempt < maxRetries) {
          retryAttempt++;
          if (isMounted) setRetryCount(retryAttempt);
          setTimeout(fetchFeeData, Math.pow(2, retryAttempt) * 500);
        } else {
          if (isMounted) { setError(userFriendlyMessage); setIsLoading(false); setRetryCount(retryAttempt); }
        }
      }
    };
    if (prescriptionId && clinicId) fetchFeeData();
    return () => { isMounted = false; if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current); };
  }, [prescriptionId, clinicId, retryTrigger]);

  const handleRetry = () => {
    setError(null); setFeeData(null); setRetryCount(0); setLastErrorCode(null);
    setRetryTrigger(prev => prev + 1);
  };

  const handleCloseCase = async () => {
    try {
      setProcessingPayment(true);
      await axios.post(buildApiUrl(`prescriptions/${prescriptionId}/close-case`), {}, { headers: { 'X-Clinic-Id': clinicId } });
      setStep('SELECT_METHOD');
    } catch (err) {
      setError(err.response?.data?.message || `Failed to close case: ${err.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleProcessPayment = async (paymentDetails) => {
    try {
      setProcessingPayment(true);
      const response = await axios.patch(
        buildApiUrl(`prescriptions/${prescriptionId}/payment`),
        paymentDetails, { headers: { 'X-Clinic-Id': clinicId } }
      );
      setPaymentData(response.data);
      setFeeData(prev => ({ ...prev, consultationFeePaid: paymentDetails.paidAmount, status: response.data.status }));
      setStep('COMPLETE'); setError(null);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to process payment: ${err.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleUpdatePaidFee = async (newPaidAmount) => {
    try {
      setProcessingPayment(true);
      const response = await axios.patch(
        buildApiUrl(`prescriptions/${prescriptionId}/paid-fee`),
        { paidAmount: newPaidAmount }, { headers: { 'X-Clinic-Id': clinicId } }
      );
      setFeeData(prev => ({ ...prev, consultationFeePaid: newPaidAmount, status: response.data.status }));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to update fee: ${err.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  const patientInitials = patientName ? patientName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'Pt';

  const stepDots = ['OVERVIEW', 'SELECT_METHOD', 'COMPLETE'];
  const currentStepIdx = stepDots.indexOf(step);

  // ─── LOADING ───
  if (isLoading) {
    return (
      <div style={{
        background: '#fff', borderRadius: '22px', padding: '48px 40px',
        textAlign: 'center', maxWidth: '420px', width: '100%',
        boxShadow: '0 24px 64px rgba(8,13,28,0.12)',
        border: '1px solid rgba(0,0,0,0.06)',
        animation: 'ppw-pop 0.3s cubic-bezier(0.16,1,0.3,1)',
      }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          border: '3px solid rgba(59,130,246,0.15)', borderTopColor: '#2563eb',
          animation: 'ppw-spin 0.8s linear infinite',
          margin: '0 auto 20px',
        }} />
        <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', marginBottom: '8px', letterSpacing: '-0.3px' }}>
          Loading Payment Details
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>Fetching consultation fee…</div>
        <style>{`
          @keyframes ppw-spin { to { transform: rotate(360deg); } }
          @keyframes ppw-pop { 0% { transform: scale(0.88); opacity: 0; } 60% { transform: scale(1.03); } 100% { transform: scale(1); opacity: 1; } }
          @keyframes ppw-slide { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes ppw-bounce { 0%,100% { transform: scale(1); } 50% { transform: scale(1.12); } }
        `}</style>
      </div>
    );
  }

  // ─── ERROR ───
  if (!feeData) {
    return (
      <div style={{
        background: '#fff', borderRadius: '22px', padding: '36px 32px',
        textAlign: 'center', maxWidth: '520px', width: '100%',
        boxShadow: '0 24px 64px rgba(8,13,28,0.12)',
        border: '1.5px solid rgba(239,68,68,0.15)',
        animation: 'ppw-pop 0.3s cubic-bezier(0.16,1,0.3,1)',
      }}>
        <style>{`
          @keyframes ppw-spin { to { transform: rotate(360deg); } }
          @keyframes ppw-pop { 0% { transform: scale(0.88); opacity: 0; } 60% { transform: scale(1.03); } 100% { transform: scale(1); opacity: 1; } }
        `}</style>
        <div style={{
          width: '64px', height: '64px', borderRadius: '18px',
          background: 'rgba(239,68,68,0.08)', border: '1.5px solid rgba(239,68,68,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '28px', margin: '0 auto 18px',
        }}><AlertTriangle size={32} color="#ef4444" /></div>
        <div style={{ fontSize: '18px', fontWeight: 800, color: '#ef4444', marginBottom: '10px', letterSpacing: '-0.4px' }}>
          Failed to Load
        </div>
        <div style={{
          fontSize: '13px', color: '#64748b', lineHeight: 1.65, marginBottom: '20px',
          background: '#f8faff', padding: '14px 16px', borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.06)', textAlign: 'left',
        }}>
          <div style={{ fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
            {error || 'Unable to fetch fee details'}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace", display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span>RX #{prescriptionId} · Clinic {clinicId}</span>
            {retryCount > 0 && <span>Retried {retryCount}/3 times</span>}
            {lastErrorCode && <span>Error: {lastErrorCode}</span>}
          </div>
        </div>
        <div style={{
          background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: '12px', padding: '14px', marginBottom: '20px', textAlign: 'left',
          fontSize: '12px', color: '#0369a1', lineHeight: 1.7,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <ClipboardList size={16} />
            <strong>Troubleshooting</strong>
          </div>
          <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <li>Verify prescription #{prescriptionId} exists</li>
            <li>Confirm clinic ID {clinicId} is correct</li>
            <li>Check that the configured backend service is reachable</li>
            {lastErrorCode === 'UNAUTHORIZED_ACCESS' && <li><strong>Prescription belongs to a different clinic</strong></li>}
          </ul>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handleRetry} disabled={isLoading} style={{
            flex: 1, padding: '11px', borderRadius: '12px',
            border: '1.5px solid #2563eb', background: 'rgba(14,165,233,0.05)',
            color: '#2563eb', fontWeight: 700, fontSize: '13px',
            cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
            fontFamily: "'Outfit', sans-serif",
          }}
          onMouseEnter={e => { if (!isLoading) { e.target.style.background = '#2563eb'; e.target.style.color = '#fff'; } }}
          onMouseLeave={e => { e.target.style.background = 'rgba(14,165,233,0.05)'; e.target.style.color = '#2563eb'; }}>
            <RefreshCw size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Retry
          </button>
          <button onClick={onClose} style={{
            flex: 1, padding: '11px', borderRadius: '12px',
            border: '1.5px solid rgba(0,0,0,0.08)', background: '#f1f5f9',
            color: '#475569', fontWeight: 700, fontSize: '13px',
            cursor: 'pointer', transition: 'all 0.2s',
            fontFamily: "'Outfit', sans-serif",
          }}
          onMouseEnter={e => { e.target.style.background = '#e2e8f0'; }}
          onMouseLeave={e => { e.target.style.background = '#f1f5f9'; }}>
            <X size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Close
          </button>
        </div>
      </div>
    );
  }

  // ─── MAIN MODAL ───
  return (
    <div style={{
      background: '#fff',
      borderRadius: '24px',
      width: '100%',
      maxWidth: '560px',
      boxShadow: '0 32px 80px rgba(8,13,28,0.14), 0 8px 24px rgba(59,130,246,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      overflow: 'hidden',
      animation: 'ppw-pop 0.3s cubic-bezier(0.16,1,0.3,1)',
    }}>
      <style>{`
        @keyframes ppw-pop { 0% { transform: scale(0.88); opacity: 0; } 60% { transform: scale(1.03); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes ppw-slide { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ppw-spin { to { transform: rotate(360deg); } }
        @keyframes ppw-bounce { 0%,100% { transform: scale(1); } 50% { transform: scale(1.12); } }
        @keyframes ppw-check { from { stroke-dashoffset: 40; } to { stroke-dashoffset: 0; } }
      `}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #080d1c, #0c1428)',
        padding: '20px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(14,165,233,0.1), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-30px', left: '30%', width: '120px', height: '120px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.08), transparent 70%)', pointerEvents: 'none' }} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
            <div style={{
              width: '46px', height: '46px', borderRadius: '14px',
              background: 'linear-gradient(135deg, #2563eb, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '15px', fontWeight: 800, color: '#fff',
              boxShadow: '0 4px 14px rgba(14,165,233,0.35)',
              flexShrink: 0, letterSpacing: '-0.5px',
            }}>
              {patientInitials}
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: '#f0f4ff', letterSpacing: '-0.4px' }}>
                Payment Workflow
              </div>
              <div style={{ fontSize: '11.5px', color: 'rgba(180,210,255,0.55)', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                <span>{patientName}</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <code style={{ fontFamily: "'JetBrains Mono', monospace", background: 'rgba(59,130,246,0.15)', color: '#38bdf8', padding: '1px 7px', borderRadius: '5px', fontSize: '10.5px', fontWeight: 600, border: '1px solid rgba(14,165,233,0.2)' }}>
                  RX #{prescriptionId}
                </code>
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{
            width: '32px', height: '32px', borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.24)',
            background: 'rgba(255,255,255,0.22)',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '14px', transition: 'all 0.18s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; e.currentTarget.style.color = '#fff'; }}>
            <X size={16} />
          </button>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', position: 'relative' }}>
          {['Overview', 'Payment', 'Complete'].map((label, i) => {
            const active = i === currentStepIdx;
            const done = i < currentStepIdx;
            return (
              <React.Fragment key={label}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: active || done ? 1 : 0.4 }}>
                  <div style={{
                    width: '20px', height: '20px', borderRadius: '6px',
                    background: done ? '#10b981' : active ? '#2563eb' : 'rgba(255,255,255,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '10px', fontWeight: 800, color: '#fff',
                    transition: 'all 0.3s',
                  }}>
                    {done ? <Check size={12} /> : i + 1}
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: active ? '#f0f4ff' : 'rgba(180,210,255,0.6)' }}>
                    {label}
                  </span>
                </div>
                {i < 2 && (
                  <div style={{ flex: 1, height: '1px', background: done ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.1)', transition: 'all 0.3s' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Accent strip */}
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #2563eb, #6366f1, #10b981)' }} />

      {/* Body */}
      <div style={{ padding: '24px', maxHeight: '60vh', overflowY: 'auto' }}>
        {/* Error Alert */}
        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.05)', border: '1.5px solid rgba(239,68,68,0.2)',
            borderRadius: '12px', padding: '13px 15px', marginBottom: '18px',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
            animation: 'ppw-slide 0.3s ease',
          }}>
            <span style={{ flexShrink: 0, lineHeight: 1, marginTop: '1px' }}><AlertTriangle size={18} color="#dc2626" /></span>
            <div style={{ flex: 1, fontSize: '13px', color: '#b91c1c', lineHeight: 1.5 }}>
              <strong style={{ display: 'block', marginBottom: '3px' }}>Error</strong>
              {error}
            </div>
            <button onClick={() => setError(null)} style={{
              background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer',
              fontSize: '16px', padding: 0, flexShrink: 0, lineHeight: 1, transition: 'transform 0.2s',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => e.target.style.transform = 'rotate(90deg)'}
            onMouseLeave={e => e.target.style.transform = 'none'}><X size={18} /></button>
          </div>
        )}

        {/* STEP: OVERVIEW */}
        {step === 'OVERVIEW' && (
          <div style={{ animation: 'ppw-slide 0.25s ease' }}>
            <PaymentStatus
              status={feeData.status}
              paymentMethod={feeData.paymentMethod}
              paidAmount={feeData.consultationFeePaid}
              assignedAmount={feeData.consultationFeeAssigned}
            />
            <div style={{
              margin: '18px 0',
              padding: '14px 16px',
              background: 'rgba(14,165,233,0.04)',
              borderRadius: '12px',
              border: '1px solid rgba(14,165,233,0.12)',
              fontSize: '13px', color: '#0369a1', lineHeight: 1.6,
              display: 'flex', alignItems: 'flex-start', gap: '9px',
            }}>
              <ClipboardList size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <strong>Next step:</strong> Consultation complete. Proceed to collect payment from the patient.
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={onClose} disabled={processingPayment} style={{
                flex: 1, padding: '12px',
                background: '#f1f5f9', color: '#475569',
                border: '1.5px solid rgba(0,0,0,0.07)',
                borderRadius: '12px', fontSize: '13px', fontWeight: 600,
                cursor: processingPayment ? 'not-allowed' : 'pointer',
                opacity: processingPayment ? 0.5 : 1, transition: 'all 0.2s',
                fontFamily: "'Outfit', sans-serif",
              }}
              onMouseEnter={e => { if (!processingPayment) e.target.style.background = '#e2e8f0'; }}
              onMouseLeave={e => { e.target.style.background = '#f1f5f9'; }}>
                Skip Payment
              </button>
              <button onClick={handleCloseCase} disabled={processingPayment} style={{
                flex: 2, padding: '12px',
                background: processingPayment ? '#94a3b8' : 'linear-gradient(135deg, #2563eb, #1e40af)',
                color: '#fff', border: 'none',
                borderRadius: '12px', fontSize: '13px', fontWeight: 700,
                cursor: processingPayment ? 'not-allowed' : 'pointer',
                opacity: processingPayment ? 0.7 : 1,
                boxShadow: processingPayment ? 'none' : '0 4px 14px rgba(14,165,233,0.35)',
                transition: 'all 0.2s',
                fontFamily: "'Outfit', sans-serif",
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              }}
              onMouseEnter={e => { if (!processingPayment) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(14,165,233,0.4)'; } }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,165,233,0.35)'; }}>
                {processingPayment ? (
                  <><span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'ppw-spin 0.7s linear infinite' }} />Processing…</>
                ) : <><CreditCard size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Proceed to Payment <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: 'middle' }} /></>}
              </button>
            </div>
          </div>
        )}

        {/* STEP: SELECT_METHOD */}
        {step === 'SELECT_METHOD' && (
          <div style={{ animation: 'ppw-slide 0.25s ease' }}>
            <ConsultationFeeDisplay
              assignedFee={feeData.consultationFeeAssigned}
              paidFee={feeData.consultationFeePaid}
              status={feeData.status}
              onEditPaidFee={handleUpdatePaidFee}
              isLoading={processingPayment}
            />
            <div style={{ marginTop: '16px' }}>
              <PaymentMethodModal
                consultationFeeAssigned={feeData.consultationFeeAssigned}
                onConfirm={handleProcessPayment}
                onCancel={() => setStep('OVERVIEW')}
                isLoading={processingPayment}
              />
            </div>
          </div>
        )}

        {/* STEP: COMPLETE */}
        {step === 'COMPLETE' && (
          <div style={{ animation: 'ppw-slide 0.25s ease' }}>
            {/* Success banner */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(16,185,129,0.03))',
              border: '1.5px solid rgba(16,185,129,0.25)',
              borderRadius: '16px',
              padding: '24px',
              textAlign: 'center',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%',
                background: '#10b981',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(16,185,129,0.3)',
                animation: 'ppw-bounce 0.6s ease',
              }}><CheckCircle2 size={30} color="#fff" /></div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: '#059669', marginBottom: '14px', letterSpacing: '-0.4px' }}>
                Payment Received!
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
                background: 'rgba(255,255,255,0.7)', padding: '14px', borderRadius: '12px',
                border: '1px solid rgba(16,185,129,0.15)',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Method</div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    <CreditCard size={14} />
                    {paymentData?.paymentMethod === 'CASH' ? 'Cash' : 'UPI'}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>Amount</div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#10b981', fontFamily: "'JetBrains Mono', monospace" }}>
                    ₹{parseFloat(paymentData?.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
              {paymentData?.notes && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                  <FileText size={14} />
                  <span>{paymentData.notes}</span>
                </div>
              )}
            </div>

            <ConsultationFeeDisplay
              assignedFee={feeData.consultationFeeAssigned}
              paidFee={feeData.consultationFeePaid}
              status={feeData.status}
              onEditPaidFee={handleUpdatePaidFee}
              isLoading={processingPayment}
            />

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => setStep('SELECT_METHOD')} style={{
                flex: 1, padding: '12px',
                background: '#f1f5f9', color: '#475569',
                border: '1.5px solid rgba(0,0,0,0.07)',
                borderRadius: '12px', fontSize: '13px', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.2s',
                fontFamily: "'Outfit', sans-serif",
              }}
              onMouseEnter={e => { e.target.style.background = '#e2e8f0'; }}
              onMouseLeave={e => { e.target.style.background = '#f1f5f9'; }}>
                <ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Back
              </button>
              <button onClick={onPaymentComplete} style={{
                flex: 2, padding: '12px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff', border: 'none',
                borderRadius: '12px', fontSize: '13px', fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                transition: 'all 0.2s',
                fontFamily: "'Outfit', sans-serif",
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(16,185,129,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(16,185,129,0.35)'; }}>
                <Check size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} /> Done & Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PrescriptionPaymentWorkflow;
